<template>
  <a-modal
    v-model:open="visible"
    title="加密上传文件"
    :footer="null"
    width="720px"
    :mask-closable="false"
    @cancel="handleCancel"
  >
    <!-- 上传拖拽区域 -->
    <a-upload-dragger
      v-model:file-list="fileList"
      :before-upload="beforeUpload"
      :accept="accept"
      :multiple="multiple"
      :max-count="maxCount"
      :show-upload-list="true"
      @remove="handleRemove"
    >
      <div class="upload-box">
        <div class="upload-icon-wrap">
          <img
            src="@/assets/casesearchImg/i-up.png"
            class="up-icon"
          />
        </div>
        <div class="upload-text-main">点击或拖拽文件到此处</div>
        <div class="upload-text-desc">支持 Word 文档（.doc/.docx），单个文件不超过 5KB</div>
        <div class="upload-text-tip">文件将进行加密传输与安全解析</div>
      </div>
    </a-upload-dragger>

    <!-- 底部按钮 -->
    <div class="modal-footer">
      <a-button @click="handleCancel" style="color: #31425E;background: #FFFFFF;box-shadow: 0px 28px 70px 0px rgba(12,31,68,0.25);border-radius: 10px 10px 10px 10px;">取消</a-button>
      <a-button
        type="primary"
        @click="handleConfirm"
        :disabled="fileList.length === 0"
        class="btns"
      >
        上传{{ fileList.length > 0 ? `(${fileList.length})` : '' }}
      </a-button>
    </div>
  </a-modal>
</template>

<script setup>
import { ref, defineEmits, defineProps, watch } from "vue";
import { message } from "ant-design-vue";
import { UploadAndExtract } from '../../api/case/case'

const props = defineProps({
  open: {
    type: Boolean,
    default: false
  },
  // 允许的文件类型
  accept: {
    type: String,
    default: ".doc,.docx"
  },
  // 是否支持多选
  multiple: {
    type: Boolean,
    default: true
  },
  // 最大文件数量
  maxCount: {
    type: Number,
    default: 10
  },
  // 单个文件大小限制（MB）
  maxSize: {
    type: Number,
    default: 50
  }
})
const emit = defineEmits(["update:open", "upload-success"])

// 内部同步弹窗显示
const visible = ref(props.open)
// 文件列表
const fileList = ref([])

// 监听外部open变化
watch(() => props.open, (val) => {
  visible.value = val
  // 打开时清空已选文件
  if (val) {
    fileList.value = []
  }
}, { immediate: true })

// 关闭弹窗
const handleCancel = () => {
  visible.value = false
  emit("update:open", false)
}

// 选择文件前校验，返回 false 阻止自动上传
const beforeUpload = (file) => {
  // 文件大小校验
  const isLtMaxSize = file.size / 1024 / 1024 < props.maxSize
  if (!isLtMaxSize) {
    message.error(`文件「${file.name}」超过 ${props.maxSize}MB，无法上传`)
    return false
  }

  // 加入待上传列表（a-upload 会自动加入 file-list，这里仅做校验）
  return false
}

// 删除文件
const handleRemove = (file) => {
  fileList.value = fileList.value.filter(item => item.uid !== file.uid)
  return false
}

// 确认上传，调用接口并返回提取的内容
const handleConfirm = async () => {
  if (fileList.value.length === 0) {
    message.warning('请先选择文件')
    return
  }

  const file = fileList.value[0].originFileObj
  const formData = new FormData()
  formData.append('file', file)

  try {
    const res = await UploadAndExtract(formData)
    if (res.isSuccess && res.data) {
      message.success('文件解析成功')
      emit('upload-success', res.data)
      // 关闭弹窗
      visible.value = false
      emit("update:open", false)
      fileList.value = []
    } else {
      message.error(res.msg || '文件解析失败')
    }
  } catch (err) {
    console.error('上传解析失败：', err)
    message.error('上传解析失败')
  }
}
</script>

<style scoped lang="less">
.upload-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 40px 20px;
  .upload-icon-wrap {
    width: 54px;
    height: 54px;
    background: #EAF2FF;
    box-shadow: 0px 28px 70px 0px rgba(12,31,68,0.25);
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 8px;
  }
  .up-icon{
    width: 24px;
    height: 24px;
  }
  .upload-text-main {
    font-size: 20px;
    font-weight: 500;
    color: #1F2329;
  }
  .upload-text-desc {
    font-size: 15px;
    color: #65738B;
  }
  .upload-text-tip {
    font-size: 14px;
    color: #86909C;
  }
}

// 美化 a-upload-dragger 边框样式
:deep(.ant-upload-drag) {
  border: 1px dashed #DFE6F2;
  border-radius: 12px;
  background: #FAFCFF;
  transition: border-color 0.3s;
  &:hover {
    border-color: #1768FF;
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  .ant-btn {
    width: 120px;
    height: 44px;
    font-weight: 600;
    font-size: 16px;
  }
}
.btns{
  background: linear-gradient( 157deg, #1568FF 0%, #0F5CE6 100%);
  box-shadow: 0px 28px 70px 0px rgba(12,31,68,0.25), 0px 8px 18px 0px rgba(23,104,255,0.2);
  border-radius: 10px 10px 10px 10px;
  border: 1px solid #1768FF;
  font-weight: 600;
  font-size: 16px;
  color: #FFFFFF;
  line-height: 22px;
  &:disabled {
    opacity: 0.5;
  }
}
</style>
